/**
 * NDT / Inspections display helpers: status skins, kind labels, the unit
 * formatter, who may press which write, and the source-log refusal messages.
 *
 * NOTHING HERE CONVERTS OR COMPUTES. The API returns lengths, activity and dose
 * in the tenant's unit and says which in `meta.units`; it computes every
 * status (cert, qual, leak test, calibration), the current source activity and
 * the investigation flag against the tenant's today. A screen prints what it
 * was handed. A client-side decay or a GBq→Ci on submit would be a second
 * answer to one question (CLAUDE.md non-negotiable 5).
 */
import type {
  NdtCalibrationStatus,
  NdtExpiryStatus,
  NdtInstrumentKind,
  NdtLeakTestStatus,
  NdtRegisterSourceEvent,
  NdtSourceStatus,
  NdtWelderQualStatus,
} from '../types/ndt-inspection';
import { fmtQty } from './display-labels';

export interface NdtSkin {
  label: string;
  fill: string;
  rail: string;
}

/** An unknown value claims nothing: a slate `—` and a transparent rail, never a real status. */
export const NDT_UNKNOWN_SKIN: NdtSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

const pick = <K extends string>(map: Record<K, NdtSkin>, value: string | null | undefined): NdtSkin =>
  value && Object.prototype.hasOwnProperty.call(map, value) ? map[value as K] : NDT_UNKNOWN_SKIN;

/**
 * Expiry standing — technician certs, welder quals, leak tests. `none` is
 * slate and says what it is: no expiry on file is a gap in the data, not a
 * clean bill of health.
 */
export const NDT_EXPIRY_SKIN: Record<NdtExpiryStatus, NdtSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  valid: { label: 'Valid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  none: { label: 'No expiry', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtExpirySkin = (v: string | null | undefined) => pick(NDT_EXPIRY_SKIN, v);

/**
 * A source's leak test: the expiry words plus FAILED — the newest test on the
 * shared radiation register failed (api#1335). Red, like expired: either way
 * the source may not be used until it passes a new test.
 */
export const NDT_LEAK_TEST_SKIN = {
  ...NDT_EXPIRY_SKIN,
  failed: { label: 'Failed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
} satisfies Record<NdtLeakTestStatus, NdtSkin>;
export const ndtLeakTestSkin = (v: string | null | undefined) => pick(NDT_LEAK_TEST_SKIN, v);

/**
 * A welder qualification's standing, and its continuity: the expiry words plus
 * LAPSED. "Expired" is only ever a past expiry day; a continuity lapse reads
 * "Lapsed" on every screen (qual row, welder header, register, Overview).
 */
export const NDT_QUAL_SKIN: Record<NdtWelderQualStatus, NdtSkin> = {
  ...NDT_EXPIRY_SKIN,
  lapsed: { label: 'Lapsed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ndtQualSkin = (v: string | null | undefined) => pick(NDT_QUAL_SKIN, v);

export const NDT_CALIBRATION_SKIN: Record<NdtCalibrationStatus, NdtSkin> = {
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  valid: { label: 'Valid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  never: { label: 'Never calibrated', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtCalibrationSkin = (v: string | null | undefined) => pick(NDT_CALIBRATION_SKIN, v);

/**
 * Source status — the shared radiation register's six (api#1335). In service
 * green (usable), in storage blue (held, not working), LOST IN HOLE red (a
 * source down a well that has not been recovered or abandoned — the one that
 * needs someone now). Abandoned in hole, returned and disposed slate — all are
 * out of the company's hands, and the label tells them apart (no new colour).
 */
export const NDT_SOURCE_STATUS_SKIN = {
  in_service: { label: 'In service', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  in_storage: { label: 'In storage', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  lost_in_hole: { label: 'Lost in hole', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  abandoned_in_hole: { label: 'Abandoned in hole', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  returned: { label: 'Returned', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  disposed: { label: 'Disposed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
} satisfies Record<NdtSourceStatus, NdtSkin>;
export const ndtSourceStatusSkin = (v: string | null | undefined) => pick(NDT_SOURCE_STATUS_SKIN, v);

export const NDT_INSTRUMENT_KIND_LABEL: Record<NdtInstrumentKind, string> = {
  exposure_device: 'Exposure device',
  survey_meter: 'Survey meter',
  ut_flaw_detector: 'UT flaw detector',
  yoke: 'MT yoke',
  densitometer: 'Densitometer',
  pmi_analyzer: 'PMI analyzer',
  other: 'Other',
};
export const ndtKindLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_INSTRUMENT_KIND_LABEL, v) ? NDT_INSTRUMENT_KIND_LABEL[v as NdtInstrumentKind] : '—';

/** The equipment `type` an Add-instrument creates, per kind — the register groups on it. */
export const NDT_KIND_EQUIPMENT_TYPE: Record<NdtInstrumentKind, string> = {
  exposure_device: 'Exposure Device',
  survey_meter: 'Survey Meter',
  ut_flaw_detector: 'UT Flaw Detector',
  yoke: 'MT Yoke',
  densitometer: 'Densitometer',
  pmi_analyzer: 'PMI Analyzer',
  other: 'NDT Instrument',
};

export const NDT_DOSIMETER_KIND_LABEL: Record<string, string> = {
  badge: 'Badge',
  tld: 'TLD',
  osl: 'OSL',
  drd: 'Direct-reading (DRD)',
  epd: 'Electronic (EPD)',
};
export const ndtDosimeterLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_DOSIMETER_KIND_LABEL, v) ? NDT_DOSIMETER_KIND_LABEL[v] : '—';

/**
 * Every event a source's log can hold on the shared register (api#1335): NDT's
 * three custody events, plus handling, lost / recovered / abandoned and
 * disposed — recorded under /radiation, or (disposed) by NDT's own edit.
 */
export const NDT_SOURCE_EVENT_LABEL = {
  check_out: 'Checked out',
  check_in: 'Checked in',
  transfer: 'Transferred',
  out_of_shield: 'Out of shield',
  in_hole: 'In hole',
  out_of_hole: 'Out of hole',
  in_shield: 'In shield',
  lost_in_hole: 'Lost in hole',
  recovered: 'Recovered',
  abandoned_in_hole: 'Abandoned in hole',
  disposed: 'Disposed',
} satisfies Record<NdtRegisterSourceEvent, string>;
export const ndtEventLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_SOURCE_EVENT_LABEL, v) ? NDT_SOURCE_EVENT_LABEL[v as NdtRegisterSourceEvent] : '—';

export const NDT_TICKET_MODE_LABEL: Record<string, string> = {
  per_day: 'One per crew-day',
  per_job: 'One per job',
};

/** "Level II"; null → `—` for a levelless credential. */
export function ndtLevelLabel(level: number | null | undefined): string {
  if (level === 1) return 'Level I';
  if (level === 2) return 'Level II';
  if (level === 3) return 'Level III';
  return '—';
}

function finite(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * A figure the API returned, with the unit it named: `1,234.5 GBq`, `0.25 in`.
 * Absent → `—`, never `0`. A real 0 prints as `0`. An absent unit prints the
 * number alone rather than a guessed label. Up to four decimals, as the API
 * rounds; no rounding beyond that.
 */
export function formatNdtValue(value: unknown, unit: string | null | undefined, maxDigits = 4): string {
  const n = finite(value);
  if (n === null) return '—';
  const text = n.toLocaleString('en-US', { maximumFractionDigits: maxDigits });
  return unit ? `${text} ${unit}` : text;
}

/**
 * A source ACTIVITY the API returned (GBq or Ci), for display: two decimals —
 * `2,663.75 GBq`, not `2,663.7465 GBq`. Formatting only; the figure is the
 * API's decayed value and nothing here converts or recomputes it. A non-zero
 * activity too small to show at two decimals keeps two significant digits
 * rather than printing a 0 it is not. Absent → `—`.
 */
export function formatNdtActivity(value: unknown, unit: string | null | undefined): string {
  const n = finite(value);
  if (n === null) return '—';
  const tiny = n !== 0 && Math.abs(n) < 0.005;
  const text = tiny
    ? n.toLocaleString('en-US', { maximumSignificantDigits: 2 })
    : n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return unit ? `${text} ${unit}` : text;
}

/**
 * True for a calendar day typed as `YYYY-MM-DD` with a FOUR-digit year that
 * names a real day. A date input accepts a six-digit year ("202620-10-20")
 * without complaint; that is not a day anyone means, so nothing is looked up
 * for it and the screen says so instead of going quiet.
 */
export function isNdtDayInput(text: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(y, mo, 0).getDate();
}

/** "License #" for a US or imperial tenant, "Licence #" otherwise. A label only. */
export function ndtLicenceLabel(country: string | null | undefined, unitSystem: string | null | undefined): string {
  return country === 'US' || unitSystem === 'imperial' ? 'License #' : 'Licence #';
}

/** A min–max range; both absent → `—`. */
export function formatNdtRange(min: unknown, max: unknown, unit: string | null | undefined): string {
  if (finite(min) === null && finite(max) === null) return '—';
  const lo = formatNdtValue(min, null);
  const hi = formatNdtValue(max, null);
  return unit ? `${lo} – ${hi} ${unit}` : `${lo} – ${hi}`;
}

/** What a number input sends: the typed figure, null when blank, NaN when not a number. */
export function numberFromInput(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** The text an edit input opens with. Absent → ''. */
export const inputValue = (value: unknown): string => {
  const n = finite(value);
  return n === null ? '' : String(n);
};

/** `In 12 days`, `3 days overdue`, `Today`. From the API's days-left; null → nothing. */
export function daysHint(days: number | null | undefined, pastWord = 'overdue'): string | null {
  if (typeof days !== 'number' || !Number.isFinite(days)) return null;
  if (days === 0) return 'Today';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} ${pastWord}`;
  return `In ${days} day${days === 1 ? '' : 's'}`;
}

// ─── Who may press what (mirrors fieldiq-api lib/ndt-roles.ts) ───────────────
//
// For hiding a button, not for enforcement. The API's requireRole is a plain
// membership test (owner is NOT admitted), so a button these refuse is left
// off rather than greyed out.

export const NDT_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const NDT_CALIBRATE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const NDT_SOURCE_LOG_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker'] as const;
export const NDT_DOSE_OFFICE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
export const canAdminNdt = inList(NDT_ADMIN_ROLES);
export const canCalibrateNdt = inList(NDT_CALIBRATE_ROLES);
export const canLogNdtSource = inList(NDT_SOURCE_LOG_ROLES);
export const seesAllNdtDoses = inList(NDT_DOSE_OFFICE_ROLES);

// ─── Source-log refusals ─────────────────────────────────────────────────────

/**
 * POST /sources/:id/logs refuses with a `code`. Each one gets a plain sentence
 * naming what to do. An unknown code falls back to the API's own `error`
 * string, then to a generic line — never to a guessed reason.
 */
export const NDT_SOURCE_LOG_MESSAGES: Record<string, string> = {
  already_checked_out: 'This source is already checked out. Check it in or transfer it instead.',
  not_checked_out: 'This source is not checked out, so it cannot be checked in or transferred.',
  not_in_service: 'This source is not in service. Set its status to In service before checking it out.',
  same_holder: 'That person already holds this source.',
  not_self: 'You can only take a source into your own custody.',
  not_holder: 'Only the person holding this source can check it in or transfer it.',
  // the shared register's event rule (api#1335)
  source_closed: 'This source is abandoned in hole or disposed. No further custody can be recorded on it.',
  source_lost: 'This source is lost in hole. Record it recovered or abandoned in the radiation register first.',
};

export function ndtApiError(err: unknown): { status: number | null; code: string | null; message: string | null } {
  const r = (err as { response?: { status?: unknown; data?: { error?: unknown; code?: unknown } } })?.response;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof r?.data?.code === 'string' ? r.data.code : null,
    message: typeof r?.data?.error === 'string' && r.data.error ? r.data.error : null,
  };
}

export function ndtSourceLogErrorMessage(err: unknown): string {
  const { code, message } = ndtApiError(err);
  if (code && Object.prototype.hasOwnProperty.call(NDT_SOURCE_LOG_MESSAGES, code)) return NDT_SOURCE_LOG_MESSAGES[code];
  return message ?? 'The custody change could not be saved.';
}

/**
 * PATCH / DELETE /sources/:id refusals that carry a `code` (api#1335) and whose
 * API sentence names a raw status key. Any other refusal — the 400s on a
 * leak-test edit, `source_checked_out` (its API sentence names the holder) —
 * shows the API's own `error`.
 */
export const NDT_SOURCE_WRITE_MESSAGES: Record<string, string> = {
  status_not_patchable:
    'This source is lost in hole, abandoned in hole or disposed. Its status changes only through the radiation register.',
  source_lost: 'This source is lost in hole. Record it recovered or abandoned in the radiation register before removing it.',
};

export function ndtSourceWriteErrorMessage(err: unknown, fallback: string): string {
  const { code, message } = ndtApiError(err);
  if (code && Object.prototype.hasOwnProperty.call(NDT_SOURCE_WRITE_MESSAGES, code)) return NDT_SOURCE_WRITE_MESSAGES[code];
  return message ?? fallback;
}

/** A write's error, from the API's `error` string when it sent one. */
export function ndtErrorMessage(err: unknown, fallback: string): string {
  return ndtApiError(err).message ?? fallback;
}

// ═══ Batch 2: jobs, welds, exams (fieldiq-api#1144) ══════════════════════════

/**
 * Job status. Scheduled blue (committed, not started), active amber (work in
 * progress), complete green, cancelled slate.
 */
export const NDT_JOB_STATUS_SKIN: Record<string, NdtSkin> = {
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  active: { label: 'Active', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  complete: { label: 'Complete', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtJobStatusSkin = (v: string | null | undefined) => pick(NDT_JOB_STATUS_SKIN, v);

/**
 * A weld's standing — the worst latest exam across its methods, decided by the
 * API. Unexamined slate (a gap, not a verdict), pending blue (shot, awaiting
 * interpretation), accept green, repair amber, cutout red.
 */
export const NDT_WELD_STANDING_SKIN: Record<string, NdtSkin> = {
  unexamined: { label: 'Unexamined', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  pending: { label: 'Pending', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  accept: { label: 'Accept', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  repair: { label: 'Repair', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  cutout: { label: 'Cut out', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ndtWeldStandingSkin = (v: string | null | undefined) => pick(NDT_WELD_STANDING_SKIN, v);

/** An exam's result: the same hues as standing (no `unexamined` — an exam exists). */
export const NDT_EXAM_RESULT_SKIN: Record<string, NdtSkin> = {
  pending: NDT_WELD_STANDING_SKIN.pending,
  accept: NDT_WELD_STANDING_SKIN.accept,
  repair: NDT_WELD_STANDING_SKIN.repair,
  cutout: NDT_WELD_STANDING_SKIN.cutout,
};
export const ndtExamResultSkin = (v: string | null | undefined) => pick(NDT_EXAM_RESULT_SKIN, v);

export const NDT_EXAM_CYCLE_LABEL: Record<string, string> = {
  original: 'Original',
  R1: 'Repair 1 (R1)',
  R2: 'Repair 2 (R2)',
  cutout: 'Cut-out',
};
export const ndtCycleLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_EXAM_CYCLE_LABEL, v) ? NDT_EXAM_CYCLE_LABEL[v] : '—';

/**
 * The welder-qualification check is a WARNING, never a refusal — the welder is
 * often the customer's or a contractor's. Qualified green, not qualified amber
 * (warn, not red: nothing is stopped), no welder slate (a gap in the record).
 */
export const NDT_QUAL_STATUS_SKIN: Record<string, NdtSkin> = {
  qualified: { label: 'Qualified', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  not_qualified: { label: 'Not qualified', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  no_welder: { label: 'No welder', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtQualStatusSkin = (v: string | null | undefined) => pick(NDT_QUAL_STATUS_SKIN, v);

/** Every reason the API can give for `not_qualified`, as plain words. */
export const NDT_QUAL_REASON_TEXT: Record<string, string> = {
  no_quals: 'The welder holds no qualification on file.',
  code_mismatch: "No qualification under this job's acceptance code.",
  process_mismatch: 'Not qualified for this welding process.',
  position_not_covered: 'The qualification does not cover this weld position.',
  diameter_out_of_range: 'The pipe diameter is outside the qualified range.',
  wall_out_of_range: 'The wall thickness is outside the qualified range.',
  qual_expired: 'The qualification had expired (or was not yet held) on the weld day.',
  continuity_lapsed: 'Process continuity had lapsed on the weld day.',
};
/** An unknown reason prints the API's code rather than a guessed sentence. */
export const ndtQualReasonText = (code: string): string =>
  Object.prototype.hasOwnProperty.call(NDT_QUAL_REASON_TEXT, code) ? NDT_QUAL_REASON_TEXT[code] : code;

/**
 * A repair rate as the API computed it. `rate` null (nothing examined yet) is
 * `—`, never `0%`; a real 0 is `0%`. Formatting only.
 */
export function formatRepairRate(rate: number | null | undefined): string {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return '—';
  return `${fmtQty(rate * 100, 1)}%`;
}

// ─── Who may press what (fieldiq-api lib/ndt-roles.ts, batch 2) ──────────────
export const NDT_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const NDT_WORK_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker'] as const;
export const canDispatchNdt = inList(NDT_DISPATCH_ROLES);
export const canWorkNdt = inList(NDT_WORK_ROLES);

// ─── Batch-2 refusals ────────────────────────────────────────────────────────

/**
 * EVERY `code` the batch-2 write routes refuse with, as a plain sentence. The
 * shot guard's codes come first (lib/ndt-shot-guard.ts), then the repair-cycle
 * rules (lib/ndt-weld-rules.ts), then the job / crew / weld / exam doors
 * (routes/ndt-inspection-jobs.routes.ts). `ndt-b2-refusals.test.ts` pins the
 * key list against those files' codes, so a code added there and not here
 * fails a test rather than falling back silently.
 *
 * An unknown code falls back to the API's own `error`, never to a guess.
 */
export const NDT_B2_REFUSAL_MESSAGES: Record<string, string> = {
  // the shot guard — technician
  tech_not_found: 'The technician is not an active employee on this account.',
  tech_not_on_crew: "The technician is not on this job's crew. Add them to the crew first.",
  tech_no_cert: 'The technician holds no certification for this method.',
  tech_cert_expired: "The technician's certification for this method was not valid on the exam day.",
  tech_level_insufficient:
    'The technician\'s level is too low for this result. Interpreting a result needs Level II or III — record the shot as Pending for a Level II to interpret.',
  // radiation safety
  tech_no_radiation_cert: 'The technician holds no radiation-safety credential, and a gamma source needs one.',
  radiation_cert_expired: "The technician's radiation-safety credential was not valid on the exam day.",
  // instrument
  instrument_required: 'This exam needs an instrument. Choose one.',
  instrument_not_found: 'That instrument is not on this account.',
  instrument_no_specs: 'That instrument has no NDT specs recorded. Set them up under NDT Instruments.',
  instrument_wrong_kind: 'That instrument is the wrong kind for this method.',
  instrument_out_of_service: 'That instrument is in maintenance or retired.',
  instrument_never_calibrated: 'That instrument had no calibration on or before the exam day.',
  instrument_overdue: "That instrument's calibration had expired by the exam day.",
  // source
  source_not_allowed: 'A radiation source is only recorded on an RT exam.',
  source_not_found: 'That source is not on this account.',
  source_not_in_service: 'That source is not in service.',
  source_leak_test_unknown: 'That source has no leak test recorded.',
  source_leak_test_failed: "That source's latest leak test failed. Take it out of use until it passes a new test.",
  source_leak_test_overdue: "That source's leak test had lapsed by the exam day.",
  source_not_checked_out: 'That source was not checked out at the time of the exam. Log the check-out first.',
  source_holder_not_on_crew: "Whoever held that source was not on this job's crew.",
  // repair cycles
  cycle_open: 'The previous exam of this weld by this method is still pending. Record its result first.',
  cycle_closed: 'This weld is finished for this method — accepted, or its cut-out was examined.',
  cycle_out_of_order: 'That is not the next cycle for this weld and method.',
  repair_limit: 'Two repairs are the limit. This exam cannot read Repair — the joint must be cut out.',
  cycle_superseded: 'A later cycle of this exam is on record, so this one can no longer change.',
  // jobs
  no_welds: 'A job needs at least one weld before it can be completed.',
  welds_unresolved: 'Some welds are not resolved yet — unexamined, pending or awaiting repair.',
  job_closed: 'This job is complete or cancelled. Nothing on it can change.',
  invalid_transition: 'A job cannot move to that status from where it is.',
  job_not_scheduled: 'Only a scheduled job can be deleted. Cancel it instead.',
  job_has_welds: 'This job has welds. Delete them first, or cancel the job.',
  job_number_contended: 'Another job was being numbered at the same moment. Try again.',
  customer_not_found: 'That customer is not on this account.',
  // crew
  already_on_crew: 'That person is already on the crew.',
  user_not_found: 'That person is not an active employee on this account.',
  // welds and exams
  duplicate_weld_number: 'A weld with that number already exists on this job.',
  duplicate_exam: 'That weld already has an exam for this method and cycle.',
  weld_has_exams: 'This weld has exams on record, so it cannot be deleted.',
  welder_not_found: 'That welder is not on this account.',
  // access
  not_on_crew: "You are not on this job's crew.",
  not_self: 'A field technician records and changes only their own exams.',
};

/** Every shot-guard warning code, in words. The API's message carries the dates. */
export const NDT_SHOT_WARNING_TEXT: Record<string, string> = {
  tech_cert_due_soon: "The technician's certification is due soon.",
  radiation_cert_due_soon: "The technician's radiation-safety credential is due soon.",
  instrument_due_soon: "The instrument's calibration is due soon.",
  source_leak_test_due_soon: "The source's leak test is due soon.",
};

export interface NdtRefusal {
  status: number | null;
  code: string | null;
  /** The plain sentence for the code. */
  message: string;
  /** Every shot-guard failure, each as a sentence (the API lists them all). */
  failures: string[];
  /** Warnings the API returned beside the refusal. */
  warnings: string[];
  /** welds_unresolved's counts, when sent. */
  weldCounts: NdtWeldCountsLike | null;
  /** cycle_out_of_order's expected cycle, when sent. */
  expected: string | null;
}

interface NdtWeldCountsLike { unexamined?: number; pending?: number; repair?: number }

const codeText = (code: string | null, fallback: string | null, generic: string) =>
  code && Object.prototype.hasOwnProperty.call(NDT_B2_REFUSAL_MESSAGES, code) ? NDT_B2_REFUSAL_MESSAGES[code] : fallback ?? generic;

/** A warning as words: the API's message when it sent one (it names the day), else the code's sentence. */
export function ndtWarningText(w: { code?: unknown; message?: unknown }): string {
  if (typeof w?.message === 'string' && w.message) return w.message;
  const code = typeof w?.code === 'string' ? w.code : '';
  return Object.prototype.hasOwnProperty.call(NDT_SHOT_WARNING_TEXT, code) ? NDT_SHOT_WARNING_TEXT[code] : code || 'A warning was returned.';
}

/**
 * Read a batch-2 write's refusal. The headline is the first code's sentence;
 * for a shot-guard 409 every failure is listed, using the API's own `error`
 * for each (it names the person, the instrument and the day) and the code's
 * sentence when one did not arrive.
 */
export function ndtRefusal(err: unknown, generic: string): NdtRefusal {
  const r = (err as { response?: { status?: unknown; data?: Record<string, unknown> } })?.response;
  const d = (r?.data ?? {}) as Record<string, unknown>;
  const code = typeof d.code === 'string' ? d.code : null;
  const apiError = typeof d.error === 'string' && d.error ? d.error : null;
  const failures = Array.isArray(d.failures)
    ? (d.failures as { code?: unknown; error?: unknown }[]).map((f) =>
        typeof f?.error === 'string' && f.error ? f.error : codeText(typeof f?.code === 'string' ? f.code : null, null, generic),
      )
    : [];
  const warnings = Array.isArray(d.warnings) ? (d.warnings as { code?: unknown; message?: unknown }[]).map(ndtWarningText) : [];
  const wc = d.weldCounts && typeof d.weldCounts === 'object' ? (d.weldCounts as NdtWeldCountsLike) : null;
  let message = codeText(code, apiError, generic);
  if (code === 'welds_unresolved' && wc) {
    message = `${message} Unexamined ${wc.unexamined ?? '—'}, pending ${wc.pending ?? '—'}, awaiting repair ${wc.repair ?? '—'}.`;
  }
  const expected = typeof d.expected === 'string' ? d.expected : null;
  if (code === 'cycle_out_of_order' && expected) message = `${message} The next one is ${ndtCycleLabel(expected)}.`;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code,
    message,
    failures,
    warnings,
    weldCounts: wc,
    expected,
  };
}

/**
 * The label for a job's legal-location field (`lsd`). "LSD" is Canada's
 * Dominion Land Survey legal subdivision and means nothing on a US job, where
 * the same field holds a county or legal description. Decided from the data
 * the screen already holds: the job's own province/state first (a two-letter
 * ISO code), then the tenant's currency as a stand-in for its country.
 */
const CA_REGIONS = new Set(['AB', 'BC', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PE', 'NL', 'YT', 'NT', 'NU']);
export type NdtLocationRef = 'ca' | 'us' | 'unknown';
export function ndtLocationRefKind(provinceState: string | null | undefined, currency?: string | null): NdtLocationRef {
  const region = (provinceState ?? '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(region)) return CA_REGIONS.has(region) ? 'ca' : 'us';
  if (currency === 'CAD') return 'ca';
  if (currency === 'USD') return 'us';
  return 'unknown';
}
export const NDT_LOCATION_REF_LABEL: Record<NdtLocationRef, string> = {
  ca: 'LSD',
  us: 'County / legal description',
  unknown: 'LSD / legal description',
};
export const ndtLocationRefLabel = (provinceState: string | null | undefined, currency?: string | null) =>
  NDT_LOCATION_REF_LABEL[ndtLocationRefKind(provinceState, currency)];

/**
 * Why "Complete" is not offered yet, from the job's own weld counts — the same
 * three buckets the API refuses on (409 welds_unresolved / no_welds). null when
 * nothing on the screen stands in the way; the server stays the authority.
 */
export function ndtCompleteBlocker(c: { total: number; unexamined: number; pending: number; repair: number } | null | undefined): string | null {
  if (!c) return null;
  if (c.total === 0) return 'Add at least one weld before completing the job.';
  const parts = [
    c.unexamined > 0 ? `${c.unexamined} unexamined` : null,
    c.pending > 0 ? `${c.pending} pending` : null,
    c.repair > 0 ? `${c.repair} awaiting repair` : null,
  ].filter(Boolean);
  const open = c.unexamined + c.pending + c.repair;
  return open > 0 ? `${open} weld${open === 1 ? ' is' : 's are'} not resolved (${parts.join(', ')}).` : null;
}
